import React, { Fragment, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { withApollo } from "react-apollo";
import moment from "moment-timezone";
import { DateRangePicker } from "react-dates";
import {
	Alert,
	Button,
	Col,
	Input,
	Label,
	Modal,
	ModalBody,
	ModalFooter,
	ModalHeader,
	Row,
} from "reactstrap";
import Select from "react-select";
import {
	findLastNominationsByIdAndDate 
} from "../../queries/Queries";
import Loading from "../Loading/Loading";
import {
	isDstTransitionDate,
	getNominationSlotsForDate,
	hasDstTransitionBetween,
	generateDstAwareTimeStamps,
	getDstInfoMessage,
	getDstDateRangeRestrictionMessage,
	getPortalTimezone,
} from "../../utils/dstUtils";

const NOMINATION_VALUES_CONFIG = 48; // 24/48/96 supported values
const NOMINATION_SLOTS_PER_HOUR = NOMINATION_VALUES_CONFIG / 24;
const NOMINATION_TIME_BEGIN = "00:00";
const NOMINATION_TIME_END = "23:30";
const NOMINATION_MAX_FUTURE_DATE_IN_DAYS = 30;
const NOMINATION_SAME_DAY_HOURS_OFFSET = 2;

/** Strip DST suffix from a timestamp string, e.g. "01:00 (2)" -> "01:00" */
const getTimeKey = (ts) => (ts ? ts.replace(/ \(\d+\)$/, "") : ts);

const CreateNomination = ({ client, isOpen, onClose, billingAccounts, mutation}) => {
	const [nominationValue, setNominationValue] = useState("");
	const [loading, setLoading] = useState(false);
	const [success, setSuccess] = useState(false);
	const [selectedBillingAccount, setSelectedBillingAccount] = useState(null);
	const [errorMessage, setErrorMessage] = useState(null);
	const [dateRange, setDateRange] = useState({
		startDate: null,
		endDate: null,
	});
	const [focus, setFocus] = useState(null);

	// DST-aware slot count & timestamps (only for same-day DST selections)
	const isSameDayDst = useMemo(() => {
		return (
			dateRange.startDate &&
			dateRange.endDate &&
			dateRange.startDate.isSame(dateRange.endDate, "day") &&
			isDstTransitionDate(dateRange.startDate)
		);
	}, [dateRange.startDate, dateRange.endDate]);

	const slotCount = useMemo(() => {
		if (isSameDayDst) {
			return getNominationSlotsForDate(dateRange.startDate, NOMINATION_SLOTS_PER_HOUR);
		}
		return NOMINATION_VALUES_CONFIG;
	}, [isSameDayDst, dateRange.startDate]);

	const timestamps = useMemo(() => {
		if (isSameDayDst) {
			return generateDstAwareTimeStamps(dateRange.startDate, NOMINATION_SLOTS_PER_HOUR, NOMINATION_TIME_BEGIN);
		}
		return generateDstAwareTimeStamps(null, NOMINATION_SLOTS_PER_HOUR, NOMINATION_TIME_BEGIN);
	}, [isSameDayDst, dateRange.startDate]);

	const [nominationValues, setNominationValues] = useState(() => [
		...Array(NOMINATION_VALUES_CONFIG).fill(""),
	]);
	const [startTime, setStartTime] = useState(NOMINATION_TIME_BEGIN);
	const [endTime, setEndTime] = useState(NOMINATION_TIME_BEGIN);
	const [firstAvailableTime, setFirstAvailableTime] = useState(
		NOMINATION_TIME_BEGIN
	);
	const [isRenomination, setIsRenomination] = useState(false);
	const [hasNoActiveNominationsForToday, setHasNoActiveNominationsForToday] =
		useState(false);
	const [hasInputValueOutsideRange, setHasInputValueOutsideRange] =
		useState(false);
	const [dstMessage, setDstMessage] = useState(null);
	const [dstRangeError, setDstRangeError] = useState(false);
	const dispatch = useDispatch();

	const handleRefetch = () => {
		// Dispatch the action to toggle refetchNominationsQuery
		dispatch({
			type: "TOGGLE_REFETCH_NOMINATIONS_QUERY",
		});
	}

	// Resize nomination values when slot count changes (DST transition)
	useEffect(() => {
		if (nominationValues.length !== slotCount) {
			setNominationValues([...Array(slotCount).fill("")]);
		}
	}, [slotCount]);

	// DST info messages & date-range validation
	useEffect(() => {
		if (dateRange.startDate && dateRange.endDate) {
			const isSameDay = dateRange.startDate.isSame(dateRange.endDate, "day");
			const startIsDst = isDstTransitionDate(dateRange.startDate);
			const endIsDst = isDstTransitionDate(dateRange.endDate);
			const dstBetween = hasDstTransitionBetween(dateRange.startDate, dateRange.endDate);

			setDstMessage(
				isSameDay ? getDstInfoMessage(dateRange.startDate) : null
			);
			// DST date is only valid when it is both the start and end (same day).
			// Any other placement (start-only, end-only, or in between) is an error.
			setDstRangeError(
				dstBetween ||
				(startIsDst && !isSameDay) ||
				(endIsDst && !isSameDay)
			);
		} else {
			setDstMessage(null);
			setDstRangeError(false);
		}
	}, [dateRange.startDate, dateRange.endDate]);

	useEffect(() => {
		const setCurrentDayNominations = async (isToday) => {
			let updatedValues = [...Array(slotCount).fill("")];

			if (selectedBillingAccount) {
				setLoading(true);

				const nominationsForDay = await client.query({
					query: findLastNominationsByIdAndDate,
					variables: {
						date: dateRange.startDate.format(moment.HTML5_FMT.DATE),
						executionIdentifier: selectedBillingAccount,
					},
					fetchPolicy: "network-only",
				});

				const response = nominationsForDay.data.findLastNominationsByIdAndDate;
				if (response.length > 0) {
					setHasNoActiveNominationsForToday(false);
					updatedValues = response.map((val) => String(val.quantity));
				} else if (isToday) {
					setHasNoActiveNominationsForToday(true);
				}
				setLoading(false);
			}
			setNominationValues(updatedValues);
		};

		const now = moment.utc(); // Current time in UTC
		const tomorrow = moment.utc().add(1, 'day').startOf('day'); // Start of tomorrow in UTC

		const isRenominationForToday = dateRange.startDate && dateRange.startDate.isSame(now, 'day');
		const isRenominationForTomorrow =
			dateRange.startDate &&
			dateRange.endDate &&
			dateRange.startDate.isSame(tomorrow, 'day') &&
			dateRange.endDate.isSame(tomorrow, 'day') &&
			now.isAfter(moment.utc().set({ hour: 12, minute: 0, second: 0 })); // Check if current time is past 12 PM UTC
		
		let earliestTime = NOMINATION_TIME_BEGIN;

		if (isRenominationForToday || isRenominationForTomorrow) {
			setIsRenomination(true);

			const offset = getTimezoneTimeWithOffset(NOMINATION_SAME_DAY_HOURS_OFFSET);

			if (isRenominationForToday) {
				setDateRange((prevState) => ({
					...prevState,
					endDate: prevState.startDate,
				}));

				if (offset.crossedMidnight) {
					// All slots for today are locked (offset spills into tomorrow)
					earliestTime = null;
				} else {
					earliestTime = timestamps.find(
						(ts) => getTimeKey(ts) > offset.time
					);
				}
			} else if (isRenominationForTomorrow) {
				if (offset.crossedMidnight) {
					// The 2-hour cutoff spills into tomorrow — find the first unlocked slot
					earliestTime = timestamps.find(
						(ts) => getTimeKey(ts) >= offset.time
					);
				}
				// else: offset doesn't cross midnight, all tomorrow slots are unlocked
			}

			setCurrentDayNominations(isRenominationForToday);
		} else {
			setIsRenomination(false);
			setHasNoActiveNominationsForToday(false);
		}

		setFirstAvailableTime(earliestTime);
		setStartTime(earliestTime);
		setEndTime(earliestTime);
	}, [dateRange.startDate, dateRange.endDate, selectedBillingAccount]);

	useEffect(() => {
		setFocus(null);
	}, [dateRange.endDate]);

	useEffect(() => {
		if (!isRenomination) {
			setNominationValues([...Array(slotCount).fill("")]);
		}
	}, [isRenomination, slotCount]);

	useEffect(() => {
		setHasInputValueOutsideRange(
			nominationValues.some((val) => isInvalidInput(val))
		);
	}, [JSON.stringify(nominationValues)]);

	const isInvalidInput = (val) =>
		parseFloat(val) < -100 || parseFloat(val) > 100;

	const submitNomination = () => {
		setLoading(true);
		setErrorMessage(null);
		const { [0]: startDateHours, [1]: startDateMins } =
			NOMINATION_TIME_BEGIN.split(":");
		const { [0]: endDateHours, [1]: endDateMins } =
			NOMINATION_TIME_END.split(":");

		const formatRequestDate = (date, hour, mins) =>
			date.set("hour", hour).set("minutes", mins).format().split("+")[0];

		const vars = {
			executionContextIdentifier: selectedBillingAccount,
			executionContextType: "BILLING_ACCOUNT",
			serviceProduct: "SERVICE_ELECTRICITY",
			startDate: formatRequestDate(
				dateRange.startDate,
				startDateHours,
				startDateMins
			),
			endDate: formatRequestDate(dateRange.endDate, endDateHours, endDateMins),
			volumes: nominationValues.map((v) => ({ volume: parseFloat(v) })),
		};

		client
			.mutate({
				mutation: mutation,
				variables: vars,
			})
			.then(() => {
				setLoading(false);
				handleRefetch();
				setSuccess(true);
			})
			.catch((e) => {
				const errorMsg = e.message;
				const msgToDisplay = errorMsg && errorMsg.split(":").pop().trim();
				if (msgToDisplay) setErrorMessage(msgToDisplay);
				setLoading(false);
			});
	};

	const isSubmitDisabled = () => {
		return (
			loading ||
			dstRangeError ||
			!selectedBillingAccount ||
			Object.values(dateRange).some((date) => date == null) ||
			nominationValues.some((nom) => nom === "" || nom == null) ||
			hasInputValueOutsideRange
		);
	};

	const toggleClose = () => {
		onClose();
	};

	const getTimezoneTimeWithOffset = (offsetHours) => {
		const tz = getPortalTimezone();
		const tzNow = moment().tz(tz);
		const tzWithOffset = tzNow.clone().add(offsetHours, "hours");
		return {
			time: tzWithOffset.format("HH:mm"),
			crossedMidnight: !tzWithOffset.isSame(tzNow, "day"),
		};
	};

	const renderBoxes = () => {
		const isInputDisabled = (i) => {
			return (
				!dateRange.startDate ||
				!dateRange.endDate ||
				hasNoActiveNominationsForToday ||
				(isRenomination && firstAvailableTime &&
					getTimeKey(timestamps[i]) < getTimeKey(firstAvailableTime)) ||
				(isRenomination && !firstAvailableTime)
			);
		};

		const updateNomValue = (index, newValue) => {
			const nextNomValues = nominationValues.map((v, i) =>
				i == index ? newValue : v
			);
			setNominationValues(nextNomValues);
		};

		const renderCell = (i) => {
			if (i >= slotCount) return null;
			return (
				<Col xs={3} sm={3} md={3} lg={3} key={`nomVal_${i}`}>
					<Label for={`nomValueInput_${i}`}>{timestamps[i]}</Label>
					<Input
						type="text"
						id={`nomValueInput_${i}`}
						value={nominationValues[i] != null ? nominationValues[i] : ""}
						disabled={isInputDisabled(i)}
						onChange={(e) => updateNomValue(i, e.target.value)}
						className={
							isInvalidInput(nominationValues[i]) ? "is-invalid" : ""
						}
					/>
				</Col>
			);
		};

		const boxes = [];
		for (let i = 0; i < slotCount; i = i + 8) {
			boxes.push(
				<Row className="nomValuesRow" key={`nomRow_${i}`}>
					<Col className="nomValuesRowCol_1" xs={6} sm={6} md={6} lg={6}>
						<Row>
							{renderCell(i)}
							{renderCell(i + 1)}
							{renderCell(i + 2)}
							{renderCell(i + 3)}
						</Row>
					</Col>
					<Col className="nomValuesRowCol_2" xs={6} sm={6} md={6} lg={6}>
						<Row>
							{renderCell(i + 4)}
							{renderCell(i + 5)}
							{renderCell(i + 6)}
							{renderCell(i + 7)}
						</Row>
					</Col>
				</Row>
			);
		}
		return boxes;
	};

	const applyNomination = () => {
		const startRange = timestamps.indexOf(startTime);
		const endRange = timestamps.indexOf(endTime);

		const updatedValues = nominationValues.map((val, i) => {
			if (i >= startRange && i <= endRange) {
				val = nominationValue;
			}
			return val;
		});

		setNominationValues(updatedValues);
		setStartTime(firstAvailableTime);
		setEndTime(firstAvailableTime);
	};

	return (
		<Modal
			isOpen={isOpen}
			toggle={toggleClose}
			size="lg"
			className="create-nomination-modal"
			backdrop="static"
		>
			<ModalHeader toggle={toggleClose}>Create Nomination</ModalHeader>
			<ModalBody>
				{success ? (
					<p>Your nomination was submitted successfully.</p>
				) : (
					<Fragment>
						{loading && <Loading />}
						{errorMessage && (
							<Row>
								<Col>
									<Alert color='danger'>{errorMessage}</Alert>
								</Col>
							</Row>
						)}
						<Row>
							<Col>
								<Label>Billing Account:</Label>
								<Input
									type="select"
									value={selectedBillingAccount}
									onChange={(e) => {
										setSelectedBillingAccount(e.target.value);
									}}
								>
									<option value="">---</option>
									{billingAccounts.map((acc) => (
										<option
											key={acc.billingAccountNumber}
											value={acc.billingAccountNumber}
										>
											{acc.billingAccountNumber}
										</option>
									))}
								</Input>
							</Col>
							<Col>
								<Label>Date:</Label>
								<DateRangePicker
									small
									startDate={dateRange.startDate}
									endDate={dateRange.endDate}
									onDatesChange={(date) => setDateRange(date)}
									displayFormat={"DD/MM/YYYY"}
									focusedInput={focus}
									onFocusChange={(focus) => setFocus(focus)}
									showClearDates={true}
									showDefaultInputIcon={true}
									noBorder={false}
									/* Two months don't fit the modal on tablets/phones; same breakpoint as the grid filters */
									numberOfMonths={window.innerWidth < 1024 ? 1 : 2}
									minimumNights={0}
									firstDayOfWeek={1}
									isOutsideRange={(day) =>
										(!day.isSame(moment(), "day") && day.isBefore(moment())) ||
										day.isAfter(
											moment().add(NOMINATION_MAX_FUTURE_DATE_IN_DAYS, "days")
										) ||
										(dateRange.startDate &&
											dateRange.startDate.isSame(moment(), "day") &&
											!day.isSame(moment(), "day"))
									}
								/>
							</Col>
						</Row>
						{(dstMessage || dstRangeError) && (
							<Row className="mt-2">
								<Col>
								{dstMessage && (
									<Alert color="info" className="mb-1">
										{dstMessage}
									</Alert>
									)}

									{dstRangeError && (
										<Alert color="danger" className="mb-1">
										A DST transition date can only be selected as both
										the start and end date (single day). Please adjust
										your date range.
										</Alert>
									)}
									<Alert color="info" className="mb-0">
										<small>
											{getDstDateRangeRestrictionMessage()}
										</small>
									</Alert>
								</Col>
							</Row>
						)}
						<Row className="justify-content-between">
							<Col>
								<Select
									clearable={false}
									value={startTime}
									options={timestamps.map((ts) => ({
										value: ts,
										label: ts,
										disabled:
											hasNoActiveNominationsForToday ||
											Object.values(dateRange).some((date) => date == null) ||
											(isRenomination && !firstAvailableTime) ||
											(isRenomination && firstAvailableTime &&
											getTimeKey(ts) < getTimeKey(firstAvailableTime)),
									}))}
									placeholder="Start Time"
									onChange={(e) => {
										setStartTime(e.value);
										setEndTime(e.value);
									}}
								/>
							</Col>
							<Col>
								<Select
									clearable={false}
									value={endTime}
									options={timestamps.map((ts) => ({
										value: ts,
										label: ts,
										disabled:
											hasNoActiveNominationsForToday ||
											Object.values(dateRange).some((date) => date == null) ||
											getTimeKey(ts) < getTimeKey(startTime) ||
											(isRenomination && !firstAvailableTime) ||
											(isRenomination && firstAvailableTime &&
												getTimeKey(ts) < getTimeKey(firstAvailableTime)),
									}))}
									placeholder="End Time"
									onChange={(e) => setEndTime(e.value)}
								/>
							</Col>
							<Col>
								<Input
									type="text"
									value={nominationValue}
									onChange={(e) => {
										setNominationValue(e.target.value);
									}}
								/>
							</Col>
							<Col>
								<Button onClick={applyNomination}>Apply</Button>
							</Col>
						</Row>
						{renderBoxes()}
						{hasInputValueOutsideRange && (
							<Alert color="danger">
								Nomination input contains invalid values. Please enter a value
								between -100 and 100.
							</Alert>
						)}
					</Fragment>
				)}
			</ModalBody>
			<ModalFooter>
				{!success && (
					<Button
						className="btn-submit-nomination"
						onClick={() => submitNomination()}
						disabled={isSubmitDisabled()}
						color="primary"
					>
						Submit
					</Button>
				)}
				<Button onClick={toggleClose} color="secondary">
					{success ? "Close" : "Cancel"}
				</Button>
			</ModalFooter>
		</Modal>
	);
};

export default withApollo(CreateNomination);
